import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import Loader from "./Loader";
import LabelWithInputField from "./LabelWithInputField";
import ResultStatus from "../classes/ResultStatus";
import CheckboxWithText from "./CheckboxWithText";
import TextView from "./TextView";
import PageNavigator from "../classes/PageNavigator";
import Result from "../classes/Result";
import AdminSignUpRequest from "../models/AdminSignUpRequest";
import FieldValidation from "../utils/FieldValidation";
import { BuildContext } from "../classes/BuildContext";

type _RegisterButtonOnPressed = (d3eState: AdminSignupPageRefs) => void;

type _LoginButtonOnPressed = (d3eState: AdminSignupPageRefs) => void;

type _UserNameFieldOnChanged = (
  text: string,
  d3eState: AdminSignupPageRefs
) => void;

type _EmailFieldOnChanged = (
  text: string,
  d3eState: AdminSignupPageRefs
) => void;

type _PasswordFieldOnChanged = (
  text: string,
  d3eState: AdminSignupPageRefs
) => void;

type _CheckBoxOnChanged = (
  text: boolean,
  d3eState: AdminSignupPageRefs
) => void;

export interface AdminSignupPageProps extends BaseUIProps {
  key?: string;
}
/// To store state data for AdminSignupPage
class AdminSignupPageRefs {
  public loginButton: LoginButtonState = new LoginButtonState();
  public registerButton: RegisterButtonState = new RegisterButtonState();
}

interface LoginButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSignupPageRefs;
  _onExistingUserHandler?: _LoginButtonOnPressed;
}

class LoginButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _LoginButtonWithState extends ObservableComponent<LoginButtonWithStateProps> {
  loginButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: LoginButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get loginButton(): LoginButtonState {
    return this.props.d3eState.loginButton;
  }
  public get d3eState(): AdminSignupPageRefs {
    return this.props.d3eState;
  }
  public get _onExistingUserHandler(): _LoginButtonOnPressed {
    return this.props._onExistingUserHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("loginButton", null, this.loginButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["loginButton"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.zero,
      child: Button({
        padding: ui.EdgeInsets.zero,
        disable: this.loginButton.disable,
        onPressed: () => {
          this._onExistingUserHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({
          data: "Login",
          style: new ui.TextStyle({ fontSize: 16 }),
          className: "x01c",
        }),
      }),
      className: "xa14",
    });
  }
}
function LoginButtonWithState(props: LoginButtonWithStateProps) {
  return React.createElement(_LoginButtonWithState, props);
}

interface RegisterButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminSignupPageRefs;
  _onSignUpFieldValidation?: _RegisterButtonOnPressed;
  showLoader: boolean;
}

class RegisterButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _RegisterButtonWithState extends ObservableComponent<RegisterButtonWithStateProps> {
  registerButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: RegisterButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get registerButton(): RegisterButtonState {
    return this.props.d3eState.registerButton;
  }
  public get showLoader(): boolean {
    return this.props.showLoader;
  }
  public get d3eState(): AdminSignupPageRefs {
    return this.props.d3eState;
  }
  public get _onSignUpFieldValidation(): _RegisterButtonOnPressed {
    return this.props._onSignUpFieldValidation;
  }
  public initState() {
    super.initState();

    this.updateObservable("registerButton", null, this.registerButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "registerButton",
        "registerButton.",
        "registerButton.hover",
        "showLoader",
      ],
      this.rebuild
    );
  }
  public registerButtonOnEnter(event): void {
    return this.registerButton.setHover(true);
  }
  public registerButtonOnExit(event): void {
    return this.registerButton.setHover(false);
  }
  public dispose(): void {
    this.registerButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      expand: true,
      child: Button({
        padding: this.registerButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.registerButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.registerButton.disable,
        onPressed: () => {
          this._onSignUpFieldValidation(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.center,
          crossAxisAlignment: ui.CrossAxisAlignment.center,
          children: [
            this.showLoader ? Loader({}) : [],
            TextView({ data: "Register", className: "xb91", key: "1" }),
          ],
          className: "xe88 hc h",
        }),
        onEnter: (event) => {
          this.registerButtonOnEnter(event);
        },
        onExit: (event) => {
          this.registerButtonOnExit(event);
        },
      }),
      className: "x1dd hc h",
    });
  }
}
function RegisterButtonWithState(props: RegisterButtonWithStateProps) {
  return React.createElement(_RegisterButtonWithState, props);
}

class _AdminSignupPageState extends ObservableComponent<AdminSignupPageProps> {
  d3eState: AdminSignupPageRefs = new AdminSignupPageRefs();
  signUpRequest: AdminSignUpRequest = null;
  emailInfo: boolean = false;
  passwordError: boolean = false;
  showLoader: boolean = false;
  isNotValidEmail: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminSignupPageProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.computeSignUpRequest();

    this.on(
      [
        "emailInfo",
        "isNotValidEmail",
        "passwordError",
        "showLoader",
        "signUpRequest",
        "signUpRequest.allowUserManagement",
        "signUpRequest.email",
        "signUpRequest.name",
        "signUpRequest.password",
      ],
      this.rebuild
    );
  }
  public setSignUpRequest(val: AdminSignUpRequest): void {
    let isValChanged: boolean = this.signUpRequest !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("signUpRequest", this.signUpRequest, val);

    this.signUpRequest = val;

    this.fire("signUpRequest", this);
  }
  public computeSignUpRequest = (): void => {
    try {
      this.setSignUpRequest(new AdminSignUpRequest());
    } catch (exception) {
      console.log(
        " exception in computeSignUpRequest : " + exception.toString()
      );

      this.setSignUpRequest(null);
    }
  };
  public setEmailInfo(val: boolean): void {
    let isValChanged: boolean = this.emailInfo !== val;

    if (!isValChanged) {
      return;
    }

    this.emailInfo = val;

    this.fire("emailInfo", this);
  }
  public setPasswordError(val: boolean): void {
    let isValChanged: boolean = this.passwordError !== val;

    if (!isValChanged) {
      return;
    }

    this.passwordError = val;

    this.fire("passwordError", this);
  }
  public setShowLoader(val: boolean): void {
    let isValChanged: boolean = this.showLoader !== val;

    if (!isValChanged) {
      return;
    }

    this.showLoader = val;

    this.fire("showLoader", this);
  }
  public setIsNotValidEmail(val: boolean): void {
    let isValChanged: boolean = this.isNotValidEmail !== val;

    if (!isValChanged) {
      return;
    }

    this.isNotValidEmail = val;

    this.fire("isNotValidEmail", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Row({
      children: [
        ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.center,
          children: [
            ui.AssetImage({
              path: "images/LoginImg.png",
              fit: ui.BoxFit.contain,
              height: 700,
              width: 700,
              className: "xe79 hc vc",
              key: "0",
            }),
          ],
          className: "x0f9 hc vc h v",
          key: "0",
        }),
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          mainAxisAlignment: ui.MainAxisAlignment.center,
          children: [
            ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              mainAxisAlignment: ui.MainAxisAlignment.center,
              children: [
                TextView({
                  data: "Register With Service Edge",
                  style: new ui.TextStyle({
                    fontSize: 28,
                    fontWeight: ui.FontWeight.w900,
                  }),
                  className: "x153 hc",
                  key: "0",
                }),
                ui.Container({
                  margin: ui.EdgeInsets.fromLTRB(
                    0.0,
                    10.0,
                    0.0,
                    0.0,
                    new Map()
                  ),
                  padding: ui.EdgeInsets.zero,
                  child: LabelWithInputField({
                    name: "Name",
                    placeHolder: "Enter your name",
                    value: this.signUpRequest.name,
                    onChanged: (text) => {
                      this.userNameFieldonChanged(text, this.d3eState);
                    },
                  }),
                  key: "1",
                  className: "xc68 hc h",
                }),
                ui.Container({
                  margin: ui.EdgeInsets.symmetric({
                    horizontal: 0.0,
                    vertical: 10.0,
                    transitions: new Map(),
                  }),
                  padding: ui.EdgeInsets.zero,
                  child: LabelWithInputField({
                    name: "Email",
                    placeHolder: "Enter your email",
                    value: this.signUpRequest.email,
                    onTap: () => {
                      this.onTapEmailFiledHandler(this.d3eState);
                    },
                    onChanged: (text) => {
                      this.emailFieldonChanged(text, this.d3eState);
                    },
                    errors: this.emailInfo
                      ? ["Please enter an email address"]
                      : this.isNotValidEmail
                      ? ["Please enter valid email address"]
                      : [],
                  }),
                  key: "2",
                  className: "x1d7 hc h",
                }),
                ui.Container({
                  margin: ui.EdgeInsets.fromLTRB(
                    0.0,
                    0.0,
                    0.0,
                    10.0,
                    new Map()
                  ),
                  padding: ui.EdgeInsets.zero,
                  child: LabelWithInputField({
                    name: "Password",
                    placeHolder: "Enter your password",
                    value: this.signUpRequest.password,
                    errors: this.passwordError
                      ? ["Please enter a password"]
                      : [],
                    onChanged: (text) => {
                      this.passwordFieldonChanged(text, this.d3eState);
                    },
                  }),
                  key: "3",
                  className: "xfbb hc h",
                }),
                ui.Container({
                  margin: ui.EdgeInsets.fromLTRB(
                    0.0,
                    0.0,
                    0.0,
                    20.0,
                    new Map()
                  ),
                  child: CheckboxWithText({
                    value: this.signUpRequest.allowUserManagement,
                    title: "Allow user management",
                    onChanged: (text) => {
                      this.checkBoxonChanged(text, this.d3eState);
                    },
                  }),
                  key: "4",
                  className: "xf7c hc h",
                }),
                ui.Row({
                  children: [
                    RegisterButtonWithState({
                      d3eState: this.d3eState,
                      _onSignUpFieldValidation: this.onSignUpFieldValidation,
                      showLoader: this.showLoader,
                      key: "0",
                    }),
                  ],
                  className: "x696 hc h",
                  key: "5",
                }),
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.center,
                  children: [
                    TextView({
                      data: "Already Registered?",
                      style: new ui.TextStyle({
                        color: cStyle.c11,
                        fontSize: cStyle.tTextViewHeadlineFiveFontSizeOn,
                        fontWeight: cStyle.tTextViewHeadlineFiveFontWeightOn,
                      }),
                      className: "x001",
                      key: "0",
                    }),
                    LoginButtonWithState({
                      d3eState: this.d3eState,
                      _onExistingUserHandler: this.onExistingUserHandler,
                      key: "1",
                    }),
                  ],
                  className: "x93 hc h",
                  key: "6",
                }),
              ],
              className: "xa5c hc h",
              key: "0",
            }),
          ],
          className: "xe3 hc vc h v",
          key: "1",
        }),
      ],
      className: ui.join(this.props.className, "AdminSignupPage hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public doAuthenticateUser = async (): Promise<void> => {
    if (
      this.signUpRequest.email.isEmpty &&
      this.signUpRequest.password.isEmpty
    ) {
      this.setEmailInfo(true);

      this.setPasswordError(true);
    }

    if (this.signUpRequest.email.isEmpty) {
      this.setEmailInfo(true);
    } else {
      this.setEmailInfo(false);
    }

    if (this.signUpRequest.password.isEmpty) {
      this.setPasswordError(true);
    } else {
      this.setPasswordError(false);
    }

    this.setShowLoader(true);

    if (FieldValidation.isEmail(this.signUpRequest.email)) {
      this.setIsNotValidEmail(false);

      let result: Result<AdminSignUpRequest> = await this.signUpRequest.save();

      if (result.status === ResultStatus.Success) {
        this.navigator.pushAdminLoginPage();
      } else {
        this.setShowLoader(false);
      }
    } else if (
      this.signUpRequest.email.isNotEmpty &&
      FieldValidation.isEmail(this.signUpRequest.email)
    ) {
      this.setIsNotValidEmail(true);
    }

    this.setShowLoader(false);
  };
  public onExistingUserHandler = (d3eState: AdminSignupPageRefs): void => {
    this.navigator.pushAdminLoginPage();
  };
  public onTapEmailFiledHandler = (d3eState: AdminSignupPageRefs): void => {
    this.setEmailInfo(false);
  };
  public onSignUpFieldValidation = (d3eState: AdminSignupPageRefs): void => {
    this.setEmailInfo(false);

    this.setIsNotValidEmail(false);

    if (this.signUpRequest.email === "") {
      this.setEmailInfo(true);
    } else {
      this.setIsNotValidEmail(
        !FieldValidation.isEmail(this.signUpRequest.email)
      );

      if (!this.isNotValidEmail) {
        this.doAuthenticateUser();
      }
    }
  };
  public userNameFieldonChanged = (
    val: string,
    d3eState: AdminSignupPageRefs
  ): void => {
    this.signUpRequest.setName(val);
  };
  public emailFieldonChanged = (
    val: string,
    d3eState: AdminSignupPageRefs
  ): void => {
    this.signUpRequest.setEmail(val);
  };
  public passwordFieldonChanged = (
    val: string,
    d3eState: AdminSignupPageRefs
  ): void => {
    this.signUpRequest.setPassword(val);
  };
  public checkBoxonChanged = (
    val: boolean,
    d3eState: AdminSignupPageRefs
  ): void => {
    this.signUpRequest.setAllowUserManagement(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get loginButton() {
    return this.d3eState.loginButton;
  }
  public get registerButton() {
    return this.d3eState.registerButton;
  }
}
export default function AdminSignupPage(props: AdminSignupPageProps) {
  return React.createElement(_AdminSignupPageState, props);
}
